import { EventName } from 'src/features/order/services/order-tealium-services';
import objTealium from 'src/utils/Tealium/Tealium';

const SELF_SERVICE = 'self service';
const PAGE_CHANNEL = 'vodapay: self service';
const PAGE_SUBDOMAIN = 'vodapay: self service'
const hostname = window.origin

type Link = {
  event_name?: EventName | `${EventName}, ${EventName}`;
  page_name?: BCAPageName | BCAPageUrl;
  link_page_name?: string;
  link_name?: string;
  link_id?: string;
  product_name?: string;
  journey_name?: JourneyName;
  product_quantity?: number;
  tender_type?: string[];
  page_url?: string;
}

export enum JourneyName {
  BCA_TOP_UP = 'bca top up',
}

export enum BCALinkID {
  GET_FINANCED = 'vodapay: smme portal: bca top up view offer',
  OFFER_CHANGE = 'vodapay: smme portal: bca top up: offer amount change',
  OFFER_REPAYMENT_PERIOD = 'vodapay: smme portal: bca top up: repay us change',
  PAYMENT_RATE_TOOLTIP = 'vodapay: smme portal: bca top up: repayment factor tooltip',
  SAVE_AND_CONTINUE = 'vodapay: smme portal: bca top up: save & continue',
  NEXT = 'vodapay: smme portal: next',
  RETRY = 'vodapay: smme: self service: error: retry'
}

export enum BCAPageName {
  GET_FINANCED = 'vodapay: smme: self service: get financed',
  BCA_TOP_UP = 'vodapay: smme: self service: bca top up',
  BCA_BUSINESS_INFORMATION = 'vodapay: smme: self service: bca business information',
  VIEW_CONTRACT = 'vodapay: smme: self service: bca view contract',
  SUCCESSFUL = 'vodapay: smme: self service: bca top up successful',
  ERROR = 'vodapay: smme: self service: error',
}

export enum BCAPageUrl {
  GET_FINANCED = '/merchant/business-cash-advance/dashboard',
  BCA_TOP_UP = '/merchant/business-cash-advance/top-up/calculation',
  VIEW_CONTRACT = '/merchant/business-cash-advance/top-up/contract'
}

type View = {
  event_name: EventName | `${EventName}, ${EventName}, ${EventName}` | `${EventName}, ${EventName}`;
  page_name?: BCAPageName;
  link_page_name?: string;
  link_name?: string;
  event_label?: string;
  event_value?: string;
  product_category?: 'POS';
  transaction_id?: string;
  event_error_name?: string;
  event_error_code?: 400;
  event_error_type?: 'user error';
  journey_type?: 'self service';
  journey_step_name?: string;
  journey_name?: string;
  page_error_name?: 'technical issue' | string;
  page_error_code?: string | number;
  page_error_type?: 'system error' | 'system code';
  form_error_type?: 'user error';
  form_error_name?: string;
  form_error_code?: string;
  form_name?: string;
  form_step?: string;
  form_step_name?: string;
  payment_preference?: string;
  page_url?: BCAPageUrl | `${string}${BCAPageUrl}`;
}

const attempt = (func: () => void) => {
  try {
    func();
  } catch (err) {
    console.error(err);
    // intentional swallowing of non functional error
  }
};

const sendLinkToTealium = (obj: Link) => {
  attempt(() => {
    objTealium.link(obj);
  });
};

const sendViewToTealium = (obj: View) => {
  attempt(() => {
    objTealium.view(obj);
  });
};

const staticTealiumVariables = () => {
  return {
    page_section: SELF_SERVICE,
    page_channel: PAGE_CHANNEL,
    page_type: 'website',
    page_subdomain: PAGE_SUBDOMAIN
  };
};

export const BCATopUpTealiumService = {
  onBCACalculationStart: () => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_START}`,
      page_name: BCAPageName.BCA_TOP_UP,
      page_url: `${hostname}${BCAPageUrl.BCA_TOP_UP}`,
      journey_name: JourneyName.BCA_TOP_UP,
      ...staticTealiumVariables()
    })
  },
  onViewContract: () => {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: BCAPageName.VIEW_CONTRACT,
      page_url: `${hostname}${BCAPageUrl.VIEW_CONTRACT}`,
      journey_name: JourneyName.BCA_TOP_UP,
      ...staticTealiumVariables()
    })
  },
  onSuccessfulBCA: () => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_END}`,
      page_name: BCAPageName.SUCCESSFUL,
      page_url: `${hostname}${BCAPageUrl.VIEW_CONTRACT}`,
      journey_name: JourneyName.BCA_TOP_UP,
      ...staticTealiumVariables()
    })
  },
  onSubmitError: (errorMessage: string, errorCode: number) => {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.PAGE_ERROR}`,
      page_name: BCAPageName.ERROR,
      page_url: `${hostname}${BCAPageUrl.VIEW_CONTRACT}`,
      journey_name: JourneyName.BCA_TOP_UP,
      page_error_name: errorMessage,
      page_error_type: 'system code',
      page_error_code: errorCode,
      ...staticTealiumVariables()
    })
  },
  onShowAlmostCompleteModal: () => {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: BCAPageName.BCA_BUSINESS_INFORMATION,
      page_url: `${hostname}${BCAPageUrl.BCA_TOP_UP}`,
      journey_name: JourneyName.BCA_TOP_UP,
      ...staticTealiumVariables()
    })
  },
  onUIInteraction: (pageName: BCAPageName, linkId: BCALinkID, pageUrl: BCAPageUrl) => {
    sendLinkToTealium({
      page_name: pageName,
      event_name: EventName.UI_INTERACTION,
      page_url: `${hostname}${pageUrl}`,
      link_id: linkId,
      ...staticTealiumVariables()
    })
  },
  onRetry: () => {
    sendLinkToTealium({
      link_page_name: BCAPageName.ERROR,
      event_name: EventName.UI_INTERACTION,
      page_url: `${hostname}${BCAPageUrl.BCA_TOP_UP}`,
      link_id: BCALinkID.RETRY,
      ...staticTealiumVariables()
    })
  },
  onOfferSliderChange: () => {
    sendLinkToTealium({
      page_name: BCAPageName.BCA_TOP_UP,
      event_name: EventName.UI_INTERACTION,
      page_url: `${hostname}${BCAPageUrl.BCA_TOP_UP}`,
      link_id: BCALinkID.OFFER_CHANGE,
      ...staticTealiumVariables()
    })
  },
  onRepaymentSliderChange: () => {
    sendLinkToTealium({
      page_name: BCAPageName.BCA_TOP_UP,
      event_name: EventName.UI_INTERACTION,
      page_url: `${hostname}${BCAPageUrl.BCA_TOP_UP}`,
      link_id: BCALinkID.OFFER_REPAYMENT_PERIOD,
      ...staticTealiumVariables()
    })
  }
}